import type { AttendanceTodaySummary } from '@micarahr/shared-types';
import { SCHEDULED_ACTOR_NAME } from '../lib/audit-labels';
import { newIdempotencyKey } from '@micarahr/api-client';
import type { AdminAttendanceRecord, AttendanceReviewHold } from './seed';
import { apiTransport } from './api-client';
import type { Page } from './collections';
import type { AttendanceFilter } from './operations';
import { narrow, ATTENDANCE_DAY_STATUSES } from './narrow';
import {
  attendanceStatusLabel,
  isAwaitingReview,
  reviewStateLabel,
  riskFlagForReviewer,
} from '../lib/labels';
import { phoneLabel, systemLabel } from './devices.api';

/**
 * Attendance operations, against the API.
 *
 * The prototype read every attendance record into the browser and filtered the
 * array. For a live attendance view that is the wrong shape twice over: the day
 * grows by one row per employee per day, and the screen is refreshed constantly,
 * so the cost is paid repeatedly rather than once.
 *
 * Risk flags are the part worth being careful about. The list carries the flag
 * codes and the score — enough to sort a queue and colour a row — and nothing
 * more. The evidence behind a flag (coordinates, distance, device details, the
 * selfie) is governed by `attendance.view_security_evidence` and is fetched
 * per record, by a caller who holds it, when a reviewer actually opens one.
 * Sending it with the list would hand every viewer of the queue the evidence
 * for every employee on it.
 */

interface ApiAttendanceEvidence {
  accuracyMeters: number | null;
  distanceMeters: number | null;
  deviceIdentifier: string | null;
  devicePlatform: string | null;
  /* Detail only: from the device registration under the same identifier. */
  deviceName?: string | null;
  deviceModel?: string | null;
  deviceOsVersion?: string | null;
  deviceRegistered: boolean | null;
  /* Detail only: the registry's word on the handset, for this employee. */
  deviceRegistrationStatus?: 'registered' | 'pending' | 'revoked' | 'none' | null;
  selfieMediaId: string | null;
  /* Detail only: whether the file behind the id can still be shown. */
  selfieStatus?: AdminAttendanceRecord['selfieStatus'];
}

interface ApiAttendanceEvent {
  id: string;
  /* The server's field is `type`; this adapter read `eventType`, which was
     never sent, so every timeline entry was labelled "undefined". */
  type: string;
  source: string;
  status: string;
  serverTime: string;
  note: string | null;
  evidence?: ApiAttendanceEvidence | null;
}

interface ApiStatusHistory {
  id: string;
  fromStatus: string | null;
  toStatus: string;
  reason: string | null;
  actorName?: string | null;
  createdAt: string;
}

interface ApiAttendanceAdjustment {
  id: string;
  kind: string;
  previousTimeIn: string | null;
  previousTimeOut: string | null;
  newTimeIn: string | null;
  newTimeOut: string | null;
  correctionRequestId?: string | null;
  createdAt: string;
}

interface ApiAttendanceRecord {
  id: string;
  employeeId: string;
  employee: {
    id: string;
    employeeNumber: string;
    fullName: string;
    department: { id: string; name: string } | null;
  } | null;
  date: string;
  status: string;
  timeIn: string | null;
  timeOut: string | null;
  workedMinutes: number;
  lateMinutes: number;
  undertimeMinutes: number;
  overtimeMinutes: number;
  breakMinutes: number;
  riskScore: number;
  riskFlags: {
    code: string;
    /* The reviewer's headline, where the wording has its own ("Phone waiting for approval"). */
    label?: string | null;
    employeeLabel?: string;
    severity?: string;
    /* Detail only, and only for a caller holding view_security_evidence. */
    reviewerDescription?: string;
    /* The entry the flag was raised on; detail only, with the evidence. */
    eventId?: string | null;
  }[];
  reviewState: string;
  reviewNote: string | null;
  reviewedAt?: string | null;
  workLocation?: { id: string; name: string } | null;
  workLocationId: string | null;
  revision: number;
  /* Detail only. The evidence on each event only for a caller holding
     view_security_evidence. */
  events?: ApiAttendanceEvent[];
  statusHistory?: ApiStatusHistory[];
  /* Detail only: what approved corrections (and review decisions) set beside the events. */
  adjustments?: ApiAttendanceAdjustment[];
  shift?: { id: string; name: string } | null;
  reviewedByName?: string | null;
  /* Detail only: who asked the employee to explain the day, when, and what. */
  explanationRequest?: {
    requestedAt: string | null;
    requestedByName: string | null;
    note: string | null;
  } | null;
  /* Detail only, while the record is in the review queue: why it was held. */
  reviewHold?: {
    rule: AttendanceReviewHold['rule'];
    flagCodes: string[];
    eventType: string | null;
    entryScore: number;
    reviewThreshold: number;
  } | null;
}

/** Flags about where an entry was made — their evidence is that entry's location. */
const LOCATION_FLAGS = new Set([
  'outside_geofence',
  'poor_gps_accuracy',
  'location_permission_denied',
  'mock_location_suspected',
  'impossible_travel',
]);

/** An entry named as a thing ("the time out"), for "At the …". */
const EVENT_NOUNS: Record<string, string> = {
  time_in: 'Time in',
  break_start: 'Break start',
  break_end: 'Break end',
  time_out: 'Time out',
};

/*
 * One phrase per entry, the history's and the server's: "Break start", as in
 * "Break start recorded". The timeline read "Break started" beside a history
 * line of "Break start recorded" for the same entry.
 */
const EVENT_LABELS: Record<string, string> = {
  time_in: 'Time in',
  break_start: 'Break start',
  break_end: 'Break end',
  time_out: 'Time out',
};

const humanize = (value: string) => value.replace(/_/g, ' ');

/** How an event reached the server, where that is not the ordinary way. */
const EVENT_SOURCE_LABELS: Record<string, string> = {
  offline_sync: 'Sent after reconnecting',
  manual_entry: 'Entered by HR',
  correction: 'From a correction',
  system: 'Recorded by the system',
};

/** Where an event stands, where that is not simply recorded. */
const EVENT_STATUS_LABELS: Record<string, string> = {
  pending_sync: 'Waiting to sync',
  pending_review: 'Awaiting review',
  rejected: 'Not accepted',
  cancelled: 'Cancelled',
  superseded: 'Replaced by a correction',
};

/**
 * The handset as a person would name it: what the employee called it, else
 * its model, else the kind of phone. Never the client's device identifier —
 * "seed-mtc-2024-0006-android" is an id, not a description.
 */
function deviceLabel(evidence: ApiAttendanceEvidence | null): string | null {
  if (!evidence) return null;
  if (!evidence.deviceIdentifier && !evidence.devicePlatform) return null;
  const name = evidence.deviceName ?? evidence.deviceModel ?? phoneLabel(evidence.devicePlatform);
  const system = systemLabel(evidence.devicePlatform, evidence.deviceOsVersion ?? null);
  // "Samsung Galaxy A15 · Android 14"; the fallback already names the platform.
  return system && (evidence.deviceName || evidence.deviceModel) ? `${name} · ${system}` : name;
}

/**
 * History reasons written before the server wrote sentences ("time_in
 * recorded"), put into the words it writes now.
 */
function historyReason(reason: string): string {
  // "HR note (Maria Santos): …" — the author is already on the line beneath.
  if (/^HR note \(.+?\): /.test(reason)) return reason.replace(/^HR note \(.+?\): /, 'HR note: ');
  // A review decision reads as the review's, beside the work status's "Status: …",
  // and by the outcome's one name — the Decision field's and the timeline's:
  // "Review: Returned for correction", never "Evidence not accepted" here and
  // "Rejected" there for the same decision.
  const review = REVIEW_REASON.exec(reason);
  if (review) {
    const [, what, rest] = review;
    const state = REVIEW_OUTCOME_STATES[what!.toLowerCase()];
    const outcome = state
      ? reviewStateLabel(state)
      : what!.charAt(0).toUpperCase() + what!.slice(1);
    // No full stop of its own: "Review: Awaiting review" and the "Status: …"
    // lines have none. A note that follows is a sentence of its own.
    return rest ? `Review: ${outcome}. ${rest}` : `Review: ${outcome}`;
  }
  return reason.replace(
    /^(time_in|time_out|break_start|break_end) recorded$/,
    (_match, type: string) => `${HISTORY_EVENT_WORDS[type] ?? humanize(type)} recorded`,
  );
}

/** "Attendance review: <outcome>. <note>" — the server's and the seed's review line. */
const REVIEW_REASON = /^Attendance review: ([^.]+)\.?\s*([\s\S]*)$/;

/**
 * The outcome phrases review lines have been written with, by the review
 * state they mean. "Evidence not accepted" is the older wording of a return
 * for correction.
 */
const REVIEW_OUTCOME_STATES: Record<string, string> = {
  'evidence accepted': 'reviewed_approved',
  'evidence not accepted': 'reviewed_rejected',
  'returned for correction': 'reviewed_rejected',
  'explanation requested': 'explanation_requested',
};

const HISTORY_EVENT_WORDS: Record<string, string> = {
  time_in: 'Time in',
  time_out: 'Time out',
  break_start: 'Break start',
  break_end: 'Break end',
};

const capitalise = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * One timeline line per event: what it was, and — only where it differs from
 * the ordinary — how it arrived and where it stands. "Time in · Sent after
 * reconnecting · Awaiting review" is the sentence a reviewer needs; "Time in ·
 * mobile · recorded" on every line is noise.
 */
export function eventLabel(
  event: ApiAttendanceEvent,
  review?: { reviewState: string; reviewedAt?: string | null },
): string {
  const parts = [EVENT_LABELS[event.type] ?? humanize(event.type)];
  if (event.source !== 'mobile') {
    parts.push(EVENT_SOURCE_LABELS[event.source] ?? capitalise(humanize(event.source)));
  }
  if (event.status === 'pending_review' && review) {
    const where = eventReviewLabel(event, review);
    if (where) parts.push(where);
  } else if (event.status !== 'recorded') {
    parts.push(EVENT_STATUS_LABELS[event.status] ?? capitalise(humanize(event.status)));
  }
  return parts.join(' · ');
}

/**
 * Where a held entry's review stands now.
 *
 * An event keeps the status it arrived with — events are what the phone
 * reported, and a decision never rewrites them — so a time in held for review
 * said "Awaiting review" for ever, beneath a panel reading "Reviewed and
 * approved". The review lives on the record; the entry reads the record's
 * answer. An entry recorded before the last decision was decided by it, so
 * while a later entry has put the day back in the queue, the earlier one
 * carries no review word rather than an outcome that is not its own.
 */
function eventReviewLabel(
  event: ApiAttendanceEvent,
  review: { reviewState: string; reviewedAt?: string | null },
): string | null {
  if (!isAwaitingReview(review.reviewState)) {
    return review.reviewState === 'not_required' ? null : reviewStateLabel(review.reviewState);
  }
  const decidedEarlier =
    !!review.reviewedAt && new Date(event.serverTime) <= new Date(review.reviewedAt);
  return decidedEarlier ? null : reviewStateLabel(review.reviewState);
}

/**
 * The statuses that are a review marker rather than how the day is going: a
 * day is parked at "Awaiting review" while its evidence is undecided, and
 * leaves it for whatever the work says next.
 */
const REVIEW_PARKED_STATUSES: ReadonlySet<string> = new Set(['pending_review', 'flagged']);

/**
 * The work status an entry gives a day, for an entry recorded while the day
 * is parked at the review marker — the server stored the marker, not the
 * status the entry would otherwise have set.
 */
const ENTRY_WORK_STATUS: Record<string, string> = {
  time_in: 'working',
  break_start: 'on_break',
  break_end: 'working',
  time_out: 'completed',
};

function entryWorkStatus(reason: string | null): string | null {
  const match =
    /^(time_in|time_out|break_start|break_end|Time in|Time out|Break start|Break end) recorded$/.exec(
      reason ?? '',
    );
  if (!match) return null;
  return ENTRY_WORK_STATUS[match[1]!.toLowerCase().replace(' ', '_')] ?? null;
}

/**
 * A history row in words.
 *
 * A note is written as an entry that leaves the status where it was, so its
 * reason is the whole story. A change of standing names what happened, then
 * the change — and says which kind of change it is. The review marker and the
 * work status share one column on the server, so "Awaiting review → On break"
 * read as the review turning into a break; a day sent for review reads
 * "Review: Awaiting review" beside the change of work status the entry made.
 *
 * Every change of work status names both ends: "Status: Working → On break".
 * A break taken while the day waited for review read "Status: On break" — the
 * row's own from-status is the review marker, so the status the day was
 * really in comes from the rows before it, which `workBefore` carries (see
 * {@link historyLabels}). A day's first row starts from "Not started".
 */
export function historyLabel(row: ApiStatusHistory, workBefore: string | null = null): string {
  const reason = row.reason ? historyReason(row.reason) : null;
  // A return for correction changes the day's status as well as its review,
  // and the Status column shows the new one ("Returned for correction"), so
  // the history says so too: "Review: Returned for correction. <note>
  // Status: Completed → Returned for correction". Left out, the history's last
  // status stayed "Completed" beside a column that no longer said it.
  const change = statusChange(row, workBefore);
  if (!change) return reason ?? `Status: ${attendanceStatusLabel(shownWork(row.toStatus))}`;
  // The reason is a sentence of its own, then the change: "…your actual time
  // in (FW7). Status: Working → Missing time out". Joined with a colon, a
  // reason that ended in a full stop read ".: Working → Missing time out".
  return reason ? `${asSentence(reason)} ${change}` : change;
}

/**
 * Every row of a day's history in words, in order, each knowing the work
 * status the day was in before it — which a row written while the day was
 * parked at the review marker cannot say for itself.
 */
export function historyLabels(rows: ApiStatusHistory[]): string[] {
  let work: string | null = null;
  return rows.map((row) => {
    const label = historyLabel(row, work);
    work = workAfter(row, work);
    return label;
  });
}

/**
 * A stored work status as the Status column shows it. "Late" and "On time"
 * are how a day being worked was started, not statuses the list shows — the
 * column reads such a day "Working" — so history reads them the same way:
 * "Status: Not started → Working", never "Not started → Late".
 */
function shownWork(status: string): string {
  return status === 'late' || status === 'on_time' ? 'working' : status;
}

/** The work status a day is in after a row: its status, or behind the review marker, the entry's. */
function workAfter(row: ApiStatusHistory, before: string | null): string | null {
  if (!REVIEW_PARKED_STATUSES.has(row.toStatus)) return shownWork(row.toStatus);
  return entryWorkStatus(row.reason) ?? workFrom(row.fromStatus, before);
}

/** The work status a row starts from: its own from-status, unless that is the review marker. */
function workFrom(from: string | null, before: string | null): string | null {
  if (from === null) return 'not_started';
  return REVIEW_PARKED_STATUSES.has(from) ? before : shownWork(from);
}

function statusChange(row: ApiStatusHistory, before: string | null): string | null {
  const { fromStatus: from, toStatus: to } = row;
  const parkedFrom = from !== null && REVIEW_PARKED_STATUSES.has(from);
  const parkedTo = REVIEW_PARKED_STATUSES.has(to);
  const start = workFrom(from, before);
  const end = parkedTo ? (entryWorkStatus(row.reason) ?? start) : shownWork(to);

  const work =
    start && end && start !== end
      ? `Status: ${attendanceStatusLabel(start)} → ${attendanceStatusLabel(end)}`
      : null;
  // Sent for review: the entry's change of work status, then the review's.
  if (parkedTo && !parkedFrom)
    return work ? `${work}. Review: Awaiting review` : 'Review: Awaiting review';
  if (parkedTo) return work;
  // "Working → Late" is no change the column would show: nothing to say.
  if (from === to || (!parkedFrom && start === end)) return null;
  // Leaving the review marker is not a transition out of "Awaiting review":
  // the review stands where it was, and the work status is what changed.
  return work ?? `Status: ${attendanceStatusLabel(end)}`;
}

/** A reason ending as a sentence ends, without doubling what it already has. */
export function asSentence(text: string): string {
  const trimmed = text.trim().replace(/[\s:;,]+$/, '');
  return /[.!?…]["'”’)]?$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * Map the API record onto the frontend's.
 *
 * The evidence fields are the careful part. The schema comment on the server's
 * evidence table states the rule outright — *null means "not evaluated", which
 * is different from false* — and the list endpoint sends no evidence at all,
 * because it is gated on `attendance.view_security_evidence` and a queue is
 * read by people who do not hold it.
 *
 * So `deviceRegistered: false` is never invented here. On the queue it is
 * null, meaning "not loaded"; on the detail it is whatever the server said,
 * including null for "the provider was never asked". A reviewer deciding
 * whether somebody falsified their location must not be shown "Not registered"
 * by a screen that simply did not fetch the answer.
 */
function toAdminRecord(row: ApiAttendanceRecord): AdminAttendanceRecord {
  /*
   * The entry whose evidence a reviewer is judging: the one a location flag
   * was raised on, else the first carrying evidence (the time-in). Showing the
   * time-in's "26 m" beside a flag raised on a Time out 2 km away read as the
   * two contradicting each other. Absent on the list, and absent on the
   * detail for a caller without the evidence permission.
   */
  const flaggedEventId = row.riskFlags.find(
    (flag) => flag.eventId && LOCATION_FLAGS.has(flag.code),
  )?.eventId;
  const evidenceEvent =
    row.events?.find((event) => event.id === flaggedEventId && event.evidence) ??
    row.events?.find((event) => event.evidence) ??
    null;
  const evidence = evidenceEvent?.evidence ?? null;
  const firstEvidenceEvent = row.events?.find((event) => event.evidence) ?? null;
  // The selfie is taken at time in; a location flag on the time out must not
  // make the panel say "Not captured".
  const selfieEvidence =
    row.events?.find((event) => event.evidence?.selfieMediaId)?.evidence ??
    firstEvidenceEvent?.evidence ??
    null;
  /*
   * The phone: the judged entry's, else the first entry that named one. An
   * entry with no device (made by HR, or a day with no entries at all) said
   * "—" over "Registration not available", as if the answer had not loaded;
   * when the evidence was read and no entry named a phone, the day says so.
   */
  const deviceEvidence = deviceLabel(evidence)
    ? evidence
    : (row.events?.find((event) => deviceLabel(event.evidence ?? null))?.evidence ?? null);
  const evidenceRead =
    !!row.events &&
    (row.events.length === 0 || row.events.some((event) => event.evidence !== undefined));
  const deviceRecorded = deviceEvidence ? true : evidenceRead ? false : null;
  /*
   * The history is what happened; the notes have sections of their own. HR's
   * notes are listed under "HR notes", and a decision's note under the
   * decision ("Returned for correction", "Explanation requested") — so the
   * history leaves out the note rows, and a decision's line drops the note
   * text that is already shown beneath it. Each note is in one place.
   */
  const shownNotes = new Set(
    [row.reviewNote, row.explanationRequest?.note]
      .map((note) => note?.trim())
      .filter((note): note is string => !!note),
  );
  const historyRows = row.statusHistory?.map((entry) => ({
    ...entry,
    reason: withoutShownNote(entry.reason, shownNotes),
  }));
  const historyText = historyRows ? historyLabels(historyRows) : [];

  return {
    id: row.id,
    employeeId: row.employeeId,
    employeeName: row.employee?.fullName ?? '',
    employeeNumber: row.employee?.employeeNumber ?? '',
    departmentId: row.employee?.department?.id ?? '',
    departmentName: row.employee?.department?.name ?? '',
    teamName: null,
    managerName: null,
    date: row.date.slice(0, 10),
    // The server stores a shift template id and no scheduled window with the
    // record, so there is no rostered shift to name here.
    shiftId: row.shift?.id ?? null,
    shiftName: row.shift?.name ?? null,
    scheduledStart: null,
    scheduledEnd: null,
    status: narrow(ATTENDANCE_DAY_STATUSES, row.status, 'attendance status'),
    // The server's outcome vocabulary is `reviewState`; see the type.
    securityDecision: null,
    // What the employee typed on the phone when they recorded the day — the
    // explanation a reviewer should read beside the evidence.
    employeeNote:
      row.events
        ?.map((event) => event.note)
        .filter(Boolean)
        .join('\n') || null,
    hrNote: row.reviewNote,
    timeIn: row.timeIn,
    timeOut: row.timeOut,
    workedMinutes: row.workedMinutes,
    lateMinutes: row.lateMinutes,
    undertimeMinutes: row.undertimeMinutes,
    overtimeMinutes: row.overtimeMinutes,
    riskScore: row.riskScore,
    /*
     * The flag headline, and on the detail its explanation.
     *
     * The code, a readable label and a severity — enough to sort a queue and
     * colour a row. The reviewer-facing description is permission-gated and
     * arrives only with the detail, for a caller holding the evidence
     * permission; elsewhere `detail` is null rather than an empty string, so
     * nothing here claims a flag came with no explanation.
     */
    riskFlags: row.riskFlags.map((flag) => {
      // The reviewer's wording, never the employee's: the stored
      // `employeeLabel` is addressed to the person holding the phone.
      const reviewer = riskFlagForReviewer(flag.code, flag);
      return {
        code: flag.code,
        label: reviewer.label,
        severity: narrowSeverity(flag.severity),
        detail: flag.reviewerDescription ?? reviewer.explanation,
      };
    }),
    reviewState: row.reviewState,
    reviewNote: row.reviewNote,
    reviewedAt: row.reviewedAt ?? null,
    // Resolved by the detail read; the list sends only an id, which is not
    // one a person recognises.
    reviewedByName: row.reviewedByName ?? null,
    explanationRequest: row.explanationRequest ?? null,
    workLocationId: row.workLocationId ?? '',
    workLocationName: row.workLocation?.name ?? null,
    revision: row.revision,

    /* Evidence — null throughout when it was not sent. See the note above. */
    deviceName: deviceRecorded === false ? NO_PHONE_ON_RECORD : deviceLabel(deviceEvidence),
    deviceRecorded,
    devicePlatform: deviceEvidence?.devicePlatform ?? null,
    deviceRegistered: deviceEvidence?.deviceRegistered ?? null,
    deviceRegistration: deviceEvidence?.deviceRegistrationStatus ?? null,
    selfieCaptured: selfieEvidence ? selfieEvidence.selfieMediaId !== null : null,
    selfieMediaId: selfieEvidence?.selfieMediaId ?? null,
    selfieStatus: selfieEvidence?.selfieStatus ?? null,
    distanceFromLocationMeters: evidence?.distanceMeters ?? null,
    gpsAccuracyMeters: evidence?.accuracyMeters ?? null,
    // Always named, the time in included: a caption shown only for a time
    // out left the reader to guess which entry an uncaptioned reading was.
    evidenceEventLabel: evidenceEvent
      ? (EVENT_NOUNS[evidenceEvent.type] ?? humanize(evidenceEvent.type))
      : null,

    events: row.events ? timelineOf(row.events, row.adjustments ?? [], row) : null,
    reviewHold: row.reviewHold ? toReviewHold(row.reviewHold) : null,
    hrNotes: row.statusHistory ? hrNotesOf(row.statusHistory) : undefined,
    history: historyRows?.flatMap((entry, index) =>
      isHrNote(entry.reason)
        ? []
        : [
            {
              id: entry.id,
              at: entry.createdAt,
              label: historyText[index] ?? historyLabel(entry),
              // A row with nobody behind it is the system's own; never a blank.
              actorName: entry.actorName || SCHEDULED_ACTOR_NAME,
            },
          ],
    ),
  };
}

const HR_NOTE = /^HR note \((.+?)\): ([\s\S]*)$/;

const isHrNote = (reason: string | null) => !!reason && HR_NOTE.test(reason);

/** A review line without the note that is shown under the decision already. */
function withoutShownNote(reason: string | null, shown: ReadonlySet<string>): string | null {
  const review = reason ? REVIEW_REASON.exec(reason) : null;
  if (!review || !review[2]?.trim() || !shown.has(review[2].trim())) return reason;
  return `Attendance review: ${review[1]}.`;
}

type TimelineEntry = NonNullable<AdminAttendanceRecord['events']>[number];

/**
 * The day's entries, with the times an approved correction set.
 *
 * A correction never adds an event — the events are what the phone reported —
 * it writes an adjustment beside them. So a day corrected to a Time out showed
 * that time out in its header and nowhere in its timeline. Each time a
 * correction set (the latest, where more than one did) is an entry of its own,
 * marked as corrected: "Time out · Corrected", its qualifier capitalised like
 * every other on the timeline ("Sent after reconnecting", "Awaiting review").
 */
export function timelineOf(
  events: ApiAttendanceEvent[],
  adjustments: ApiAttendanceAdjustment[],
  review: { reviewState: string; reviewedAt?: string | null },
): TimelineEntry[] {
  const entries: TimelineEntry[] = events.map((event) => ({
    id: event.id,
    kind: event.type,
    at: event.serverTime,
    label: eventLabel(event, review),
  }));
  const corrected = new Map<'time_in' | 'time_out', { id: string; at: string }>();
  const byTime = [...adjustments].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const adjustment of byTime) {
    if (adjustment.newTimeIn && !sameInstant(adjustment.newTimeIn, adjustment.previousTimeIn)) {
      corrected.set('time_in', { id: `${adjustment.id}:time_in`, at: adjustment.newTimeIn });
    }
    if (adjustment.newTimeOut && !sameInstant(adjustment.newTimeOut, adjustment.previousTimeOut)) {
      corrected.set('time_out', { id: `${adjustment.id}:time_out`, at: adjustment.newTimeOut });
    }
  }
  for (const [kind, entry] of corrected) {
    // Already on the timeline as an entry of its own (one entered by HR).
    if (entries.some((event) => event.kind === kind && sameInstant(event.at, entry.at))) continue;
    // What the phone reported stays, said to be replaced where it was.
    for (const original of entries) {
      if (original.kind === kind && !sameInstant(original.at, entry.at)) {
        const superseded = EVENT_STATUS_LABELS.superseded!;
        if (!original.label.includes(superseded)) original.label += ` · ${superseded}`;
      }
    }
    entries.push({ id: entry.id, kind, at: entry.at, label: `${EVENT_LABELS[kind]} · Corrected` });
  }
  return entries.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));
}

const sameInstant = (a: string | null, b: string | null) =>
  a !== null && b !== null && Date.parse(a) === Date.parse(b);

/**
 * HR's notes on a day, from its history: each written there as "HR note
 * (author): text" (see the server's addAttendanceNote), oldest first.
 */
export function hrNotesOf(
  rows: ApiStatusHistory[],
): { id: string; note: string; authorName: string | null; at: string }[] {
  return rows.flatMap((row) => {
    const match = row.reason ? HR_NOTE.exec(row.reason) : null;
    if (!match) return [];
    return [
      {
        id: row.id,
        note: match[2]!.trim(),
        authorName: row.actorName || match[1] || null,
        at: row.createdAt,
      },
    ];
  });
}

/** A day whose entries named no phone, in the words the console uses for it everywhere. */
export const NO_PHONE_ON_RECORD = 'No phone on record';

/** The server's reason for a hold, with the checks and the entry named for a reviewer. */
function toReviewHold(hold: NonNullable<ApiAttendanceRecord['reviewHold']>): AttendanceReviewHold {
  return {
    rule: hold.rule,
    flagLabels: hold.flagCodes.map((code) => riskFlagForReviewer(code).label),
    entryLabel: hold.eventType ? (EVENT_NOUNS[hold.eventType] ?? humanize(hold.eventType)) : null,
    entryScore: hold.entryScore,
    reviewThreshold: hold.reviewThreshold,
  };
}

/** Unknown severities sort as high rather than disappearing off the queue. */
function narrowSeverity(value: string | undefined): 'low' | 'medium' | 'high' {
  return value === 'low' || value === 'medium' || value === 'high' ? value : 'high';
}

const cursors = new Map<string, string[]>();

/** GET /dashboards/attendance — today's records, aggregated under the caller's scope. */
interface ApiAttendanceDashboard {
  date: string;
  scheduled: number;
  timedIn: number;
  byStatus: Record<string, number>;
  scheduledToday?: { timedIn: number; notTimedIn: number; onLeave: number; absent: number };
  timedInUnscheduled?: number;
  awaitingReview: number;
  explanationRequested?: number;
  lateArrivals?: number;
  flagged: number;
  updatedAt: string;
}

export const apiAttendanceAdminService = {
  async list(filter: AttendanceFilter): Promise<Page<AdminAttendanceRecord>> {
    const pageSize = filter.pageSize ?? 25;
    const pageNumber = filter.page ?? 1;
    // Every filter the request sends belongs in the key: the review queue and
    // the daily list page through different result sets, and sharing a cursor
    // between them lands one on the other's page two.
    const key = JSON.stringify([
      filter.employeeId,
      filter.date,
      filter.from,
      filter.to,
      filter.status,
      filter.departmentId,
      filter.search,
      Boolean(filter.flaggedOnly),
      filter.review,
      filter.shortfall,
      pageSize,
    ]);

    const known = cursors.get(key) ?? [];
    const cursor = pageNumber <= 1 ? undefined : known[Math.min(pageNumber - 2, known.length - 1)];

    const page = await apiTransport().page<ApiAttendanceRecord>('/api/v1/attendance', {
      query: {
        employeeId: filter.employeeId,
        date: filter.date,
        from: filter.from,
        to: filter.to,
        status: filter.status === 'all' ? undefined : filter.status,
        // Both were accepted by the screen and sent nowhere: the search box
        // and the department filter did nothing.
        departmentId: filter.departmentId,
        search: filter.search?.trim() || undefined,
        flaggedOnly: filter.flaggedOnly || undefined,
        review: filter.review === 'all' ? undefined : filter.review,
        shortfall: filter.shortfall === 'all' ? undefined : filter.shortfall,
        limit: pageSize,
        cursor,
      },
    });

    if (page.pageInfo.nextCursor) {
      const next = [...known];
      next[pageNumber - 1] = page.pageInfo.nextCursor;
      cursors.set(key, next);
    }

    return {
      rows: page.items.map(toAdminRecord),
      total: page.totalCount,
      page: pageNumber,
      pageSize,
    };
  },

  /**
   * One record, with whatever evidence the caller is entitled to.
   *
   * The server decides what to include: a caller without
   * `attendance.view_security_evidence` gets the record and the flag labels,
   * and the coordinates, device details and reviewer descriptions are simply
   * absent — not present-and-hidden, which a client could reveal.
   */
  async get(id: string): Promise<AdminAttendanceRecord | null> {
    try {
      return toAdminRecord(
        await apiTransport().get<ApiAttendanceRecord>(`/api/v1/attendance/${id}`),
      );
    } catch (error) {
      if ((error as { status?: number }).status === 404) return null;
      throw error;
    }
  },

  /**
   * Today's headline, from the server's aggregation rather than a whole day of
   * records delivered to the browser to be counted.
   *
   * `scheduled` is the planner's published assignments for today and
   * `timedIn` the server's count of days with a time in — not a subtraction
   * from `scheduled`, which counted everyone scheduled who had no record yet
   * as timed in. `late` counts today's days with lateness, whatever their
   * status now — the list's "Has lateness" filter for today. `pendingReview` is the server's review-state count, which is the
   * queue the review screen actually works.
   */
  async todaySummary(): Promise<AttendanceTodaySummary> {
    const data = await apiTransport().get<ApiAttendanceDashboard>('/api/v1/dashboards/attendance');
    const count = (status: string) => data.byStatus[status] ?? 0;

    // The scheduled, by where their day stands: the four add up to `scheduled`.
    const split = data.scheduledToday;
    return {
      scheduled: data.scheduled,
      timedIn: split?.timedIn ?? data.timedIn,
      notTimedIn: split?.notTimedIn,
      timedInUnscheduled: data.timedInUnscheduled ?? 0,
      late: data.lateArrivals ?? count('late'),
      absent: split?.absent ?? count('absent'),
      onLeave: split?.onLeave ?? count('on_leave'),
      pendingReview: data.awaitingReview,
      awaitingReview: data.awaitingReview - (data.explanationRequested ?? 0),
      flagged: data.flagged,
    };
  },

  /**
   * Record a review decision (POST /attendance/:id/review).
   *
   * The actor is the session's, resolved by the server; the argument exists
   * for the prototype's signature. The revision the reviewer was looking at is
   * sent, so a decision on a record somebody else has since decided is refused
   * rather than applied over theirs. Answers with the re-read record — the
   * route's own answer is the bare row, without the names the screen shows.
   */
  async review(
    id: string,
    decision: 'approve' | 'approve_with_note' | 'request_explanation' | 'return_for_correction',
    note: string,
    _actor: unknown,
    revision?: number,
  ): Promise<AdminAttendanceRecord> {
    await apiTransport().post(
      `/api/v1/attendance/${id}/review`,
      { decision, note, revision },
      { idempotencyKey: newIdempotencyKey() },
    );
    return reread(id);
  },

  /**
   * A short-lived link to a selfie (GET /media/:id).
   *
   * The server checks `attendance.view_security_evidence` again here and
   * records the viewing in the audit log; the link it returns expires in five
   * minutes, so it is fetched when the reviewer asks to see the photograph,
   * never with the record.
   */
  async selfieUrl(mediaId: string): Promise<string> {
    const media = await apiTransport().get<{ downloadUrl: string }>(`/api/v1/media/${mediaId}`);
    return media.downloadUrl;
  },

  /** Append an attributed HR note (POST /attendance/:id/notes). */
  async addNote(id: string, note: string, _actor: unknown): Promise<AdminAttendanceRecord> {
    await apiTransport().post(
      `/api/v1/attendance/${id}/notes`,
      { note },
      { idempotencyKey: newIdempotencyKey() },
    );
    return reread(id);
  },

  /** Mark a day with no time in absent (POST /attendance/:id/mark-absent). */
  async markAbsent(id: string, reason: string, _actor: unknown): Promise<AdminAttendanceRecord> {
    await apiTransport().post(
      `/api/v1/attendance/${id}/mark-absent`,
      { reason },
      { idempotencyKey: newIdempotencyKey() },
    );
    return reread(id);
  },
};

async function reread(id: string): Promise<AdminAttendanceRecord> {
  const record = await apiAttendanceAdminService.get(id);
  if (!record) throw new Error('That attendance record could not be found after the change.');
  return record;
}
